import { gsap } from 'gsap/dist/gsap.min.js';
import { CRAWL_PER_SEC, MAX_DELTA, foldCrawl } from './utils-crawl';

/**
 * FVの写真を止まらずに流す
 *
 * @description Instagramの列と同じ流し方で、それより少しゆっくり流す。PCは左へ、SPは上へ流す。
 *              ホバーでは止めない。札は同じ並びを3周ぶん並べてあり、位置は常に真ん中の周の中へ畳む
 */

/** Instagramの列に対する速さの比 */
const SPEED_RATIO = 0.75;

/** デザイン基準幅。流す速さはInstagramの列と同じく画面幅に比例させる */
const DESIGN_WIDTH = 1728;


// 生きているスライダーの後片付け。非同期遷移でMVごと作り直すとき、
// rAFループとIntersectionObserverを置き去りにしないように持っておく
let teardown: (() => void) | null = null;

/**
 * MVの写真スライダーを破棄する
 *
 * @returns {void}
 */
const destroyMvSlider = (): void => {
    teardown?.();
    teardown = null;
};

/**
 * MVの写真スライダーを初期化する
 *
 * @returns {void}
 */
const initMvSlider = (): void => {
    destroyMvSlider();

    const root = document.querySelector<HTMLElement>('[data-mv-slider]');
    const track = root?.querySelector<HTMLElement>('[data-mv-track]');

    if (!root || !track) return;

    const items = Array.from(track.children) as HTMLElement[];
    const total = items.length / 3;

    if (total < 2 || !Number.isInteger(total)) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const spMq = window.matchMedia('(max-width: 1024px)');

    let step = 0;
    let pos = 0;
    let frame = 0;
    let prev = 0;
    let visible = true;
    // スプラッシュ中は写真を伏せて待つので、流し始めを遅らせる
    let ready = false;

    /**
     * 札1枚ぶんの送り幅を測る
     *
     * @description 札の大きさは画面の縦横どちらが詰まるかで決まるので、数値では持たずに実物から取る
     * @returns {number} 送り幅(px)
     */
    const measure = (): number => (spMq.matches
        ? items[1].offsetTop - items[0].offsetTop
        : items[1].offsetLeft - items[0].offsetLeft);

    /**
     * いまの位置を書き込む
     *
     * @returns {void}
     */
    const paint = (): void => {
        pos = foldCrawl(pos, -total * step, total * step);
        const v = `${pos.toFixed(2)}px`;

        track.style.transform = spMq.matches ? `translate3d(0, ${v}, 0)` : `translate3d(${v}, 0, 0)`;
    };

    /**
     * 1フレームぶん流す
     *
     * @param {number} now rAFのタイムスタンプ
     * @returns {void}
     */
    const tick = (now: number): void => {
        // rAFの時刻はフレームの頭なので、始めた直後は performance.now() より前に来て負になることがある
        const delta = Math.max(0, Math.min((now - prev) / 1000, MAX_DELTA));
        prev = now;

        pos -= ((window.innerWidth * CRAWL_PER_SEC) / DESIGN_WIDTH) * SPEED_RATIO * delta;
        paint();

        frame = requestAnimationFrame(tick);
    };

    /**
     * 流し始める
     *
     * @returns {void}
     */
    const start = (): void => {
        if (reduced || frame || !ready || !visible) return;

        prev = performance.now();
        frame = requestAnimationFrame(tick);
    };

    /**
     * 流れを止める
     *
     * @returns {void}
     */
    const stop = (): void => {
        cancelAnimationFrame(frame);
        frame = 0;
    };

    /**
     * 画面の大きさが変わったら、流れの進み具合を保ったまま置き直す
     *
     * @returns {void}
     */
    const onResize = (): void => {
        const next = measure();

        // iOSはツールバーの出入りでもresizeが来る。札の大きさが変わらないときは触らない
        if (next === step || step === 0) return;

        pos = (pos / step) * next;
        step = next;
        paint();
    };

    step = measure();
    pos = -total * step;
    paint();

    // スプラッシュが出るときは、幕が引けるまで写真を伏せて待ち、上から引き出す
    if (document.body.classList.contains('splash-is-on')) {
        gsap.set(items, { clipPath: 'inset(0% 0% 100% 0%)' });

        document.addEventListener('splash:reveal', () => {
            // 引き出しと同時に流し始める。止まった絵が出てから動き出すと継ぎ目に見える
            ready = true;
            start();

            gsap.to(items, {
                clipPath: 'inset(0% 0% 0% 0%)',
                duration: 1.1,
                ease: 'power2.out',
                onComplete: () => {
                    gsap.set(items, { clearProps: 'clipPath' });
                },
            });
        }, { once: true });
    } else {
        ready = true;
        start();
    }

    window.addEventListener('resize', onResize, { passive: true });

    // 画面外では動かさない
    const io = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;

        if (visible) start();
        else stop();
    }, { threshold: 0 });

    io.observe(root);

    teardown = () => {
        stop();
        io.disconnect();
        window.removeEventListener('resize', onResize);
        track.style.transform = '';
    };
};

export { initMvSlider, destroyMvSlider };

document.addEventListener('DOMContentLoaded', initMvSlider);
